import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { TERMS_CONDITIONS } from '../data/termsConditions';

const Section = ({ section }) => {
  if (section.emphasized) {
    return (
      <p
        className="text-sm uppercase tracking-[0.18em] text-gray-500 leading-relaxed mb-12"
        style={{ letterSpacing: '0.06em' }}
      >
        {section.body}
      </p>
    );
  }
  return (
    <section className="mb-10">
      {section.heading && (
        <h2
          className="text-2xl md:text-3xl text-gray-900 mb-5"
          style={{ fontFamily: 'Garamond, "EB Garamond", serif' }}
        >
          {section.heading}
        </h2>
      )}
      {section.paragraphs?.map((p, idx) => (
        <p key={`${section.heading || 'p'}-${idx}`} className="text-base leading-[1.85] text-gray-800 mb-4">
          {p}
        </p>
      ))}
      {section.list && (
        <ul className="list-disc pl-6 space-y-2 text-base leading-[1.75] text-gray-800 mb-4">
          {section.list.map((item) => (
            <li key={item.slice(0, 40)}>{item}</li>
          ))}
        </ul>
      )}
      {section.afterListParagraphs?.map((p, idx) => (
        <p key={`${section.heading || 'p'}-after-${idx}`} className="text-base leading-[1.85] text-gray-800 mb-4">
          {p}
        </p>
      ))}
    </section>
  );
};

const Terms = () => {
  const { language } = useLanguage();
  const doc = TERMS_CONDITIONS[language] || TERMS_CONDITIONS.en;

  useDocumentMeta({
    title: `${doc.title} | BIMCORP MX`,
    description:
      language === 'es'
        ? 'Términos y Condiciones de uso y contratación de BIMCORP MX para los servicios digitales de constitución de sociedades y gestión corporativa en México.'
        : 'BIMCORP MX Terms and Conditions governing the use of the platform and the engagement of digital incorporation and corporate services in Mexico.',
  });

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--pearl)', color: 'var(--dark-stone)' }}
      data-testid="terms-page"
    >
      <article className="max-w-3xl mx-auto px-6 sm:px-8 py-12 md:py-20">
        {/* Top breadcrumb */}
        <nav className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-600 hover:text-blue-900 transition-colors"
            data-testid="terms-back-top"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            {language === 'es' ? 'Volver al inicio' : 'Back to home'}
          </Link>
        </nav>

        {/* Title */}
        <h1
          className="text-4xl sm:text-5xl leading-tight mb-3 text-gray-900"
          style={{ fontFamily: 'Garamond, "EB Garamond", serif' }}
        >
          {doc.title}
        </h1>
        <p className="text-sm uppercase tracking-[0.18em] text-gray-500 mb-12">
          {doc.lastUpdated}
        </p>

        {doc.sections.map((section, idx) => (
          <Section key={section.heading || `section-${idx}`} section={section} />
        ))}

        <div className="mt-16 pt-10 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-900 transition-colors"
            data-testid="terms-back-bottom"
          >
            <ChevronLeft className="w-4 h-4" />
            {language === 'es' ? 'Volver al inicio' : 'Back to home'}
          </Link>
        </div>
      </article>
    </div>
  );
};

export default Terms;
